import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookOpen, Video, FileText, Headphones, ExternalLink, Clock, Library } from "lucide-react";
import { LoadingPage } from "@/components/ui/loading-spinner";
import { EmptyState } from "@/components/ui/empty-state";
import { useLanguage } from "../contexts/LanguageContext";

const Resources = () => {
  const { token } = useContext(AuthContext);
  const { t } = useLanguage();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState("all");

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resources`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResources(response.data);
    } catch (error) {
      console.error("Error loading resources:", error);
      toast.error("Failed to load resources");
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "video": return <Video className="w-6 h-6" />;
      case "article": return <FileText className="w-6 h-6" />;
      case "ebook": return <BookOpen className="w-6 h-6" />;
      case "webinar": return <Headphones className="w-6 h-6" />;
      default: return <FileText className="w-6 h-6" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "video": return "from-purple-400 to-purple-600";
      case "article": return "from-blue-400 to-blue-600";
      case "ebook": return "from-success to-success/80";
      case "webinar": return "from-warning to-warning/80";
      default: return "from-primary to-primary/80";
    }
  };

  const filteredResources = activeType === "all" 
    ? resources 
    : resources.filter(r => r.type === activeType);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <LoadingPage message="Loading resources..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8" data-testid="resources-container">
        {/* Compact Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent to-accent/80 flex items-center justify-center shadow-md">
              <Library className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                {t('resources.title')}
              </h1>
              <p className="text-gray-600 text-sm md:text-base">
                {t('resources.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Type Filters */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="pt-6">
            <Tabs defaultValue="all" onValueChange={setActiveType}>
              <div className="sm:hidden overflow-x-auto -mx-2 px-2 pb-2">
                <TabsList className="inline-flex w-auto min-w-full bg-muted/30 p-2 gap-2">
                  <TabsTrigger 
                    value="all" 
                    className="flex-shrink-0 min-h-[44px] px-4 py-2 flex items-center justify-center whitespace-nowrap data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <Library className="w-4 h-4 mr-2" />
                    <span className="text-sm">{t('resources.allResources')}</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="video" 
                    className="flex-shrink-0 min-h-[44px] px-4 py-2 flex items-center justify-center whitespace-nowrap data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <Video className="w-4 h-4 mr-2" />
                    <span className="text-sm">{t('resources.videos')}</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="article" 
                    className="flex-shrink-0 min-h-[44px] px-4 py-2 flex items-center justify-center whitespace-nowrap data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    <span className="text-sm">{t('resources.articles')}</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="ebook" 
                    className="flex-shrink-0 min-h-[44px] px-4 py-2 flex items-center justify-center whitespace-nowrap data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    <span className="text-sm">{t('resources.ebooks')}</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="webinar" 
                    className="flex-shrink-0 min-h-[44px] px-4 py-2 flex items-center justify-center whitespace-nowrap data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <Headphones className="w-4 h-4 mr-2" />
                    <span className="text-sm">{t('resources.webinars')}</span>
                  </TabsTrigger>
                </TabsList>
              </div>
              <div className="hidden sm:block">
                <TabsList className="grid w-full grid-cols-5 gap-2 bg-muted/30 p-2">
                  <TabsTrigger 
                    value="all" 
                    className="min-h-[44px] flex items-center justify-center data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <Library className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t('resources.allResources')}</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="video" 
                    className="min-h-[44px] flex items-center justify-center data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <Video className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t('resources.videos')}</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="article" 
                    className="min-h-[44px] flex items-center justify-center data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <FileText className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t('resources.articles')}</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="ebook" 
                    className="min-h-[44px] flex items-center justify-center data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <BookOpen className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t('resources.ebooks')}</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="webinar" 
                    className="min-h-[44px] flex items-center justify-center data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <Headphones className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t('resources.webinars')}</span>
                  </TabsTrigger>
                </TabsList>
              </div>
            </Tabs>
          </CardContent>
        </Card>

        {/* Results Count */}
        {filteredResources.length > 0 && (
          <div className="mb-6">
            <p className="text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{filteredResources.length}</span> resource{filteredResources.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filteredResources.map((resource) => (
            <Card key={resource.id} className="group border-none shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden" data-testid={`resource-card-${resource.id}`}>
              {/* Gradient Top Bar */}
              <div className={`h-2 bg-gradient-to-r ${getTypeColor(resource.type)}`}></div>
              
              {/* Resource Icon Display */}
              <div className={`aspect-video bg-gradient-to-br ${getTypeColor(resource.type)} flex items-center justify-center relative overflow-hidden`}>
                <div className="absolute inset-0 bg-white/10"></div>
                <div className="relative z-10 text-white">
                  {getTypeIcon(resource.type)}
                </div>
              </div>

              <CardHeader className="pb-4">
                <div className="flex items-start justify-between mb-3 gap-2">
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-xs font-medium">
                    {resource.category}
                  </Badge>
                  <Badge variant="secondary" className="text-xs capitalize">
                    {resource.type}
                  </Badge>
                </div>
                <CardTitle className="text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2">
                  {resource.title}
                </CardTitle>
                <CardDescription className="text-sm leading-relaxed line-clamp-3">
                  {resource.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="pt-0 space-y-3">
                {resource.duration && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground p-2 bg-muted/20 rounded-lg">
                    <Clock className="w-4 h-4 text-primary" />
                    <span>{resource.duration}</span>
                  </div>
                )}
                
                <Button className="w-full shadow-md group-hover:shadow-lg transition-shadow" size="sm" asChild>
                  <a href={resource.url} target="_blank" rel="noopener noreferrer">
                    View Resource <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredResources.length === 0 && (
          <EmptyState
            icon={Library}
            title={t('resources.noResults')}
            description="Try selecting a different resource type to find what you need."
            data-testid="no-resources-message"
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Resources;